import { useEffect } from 'react'

// Duration of the `<html>` backdrop-tint fade. Lives in one place so the transition
// string and the matching restore delay can never drift apart.
const BACKDROP_TRANSITION_MS = 225

// Module-level reference count so nested/overlapping locks (e.g. a preview popper
// closing as a dialog opens) don't clobber each other. Only the first lock writes
// the body styles; only the last release restores them.
let lockCount = 0
let savedScrollY = 0
// Whether the active lock stack wrote the *animated* tint transition. The `<html>`
// background is a single shared property written once (first lock) and torn down
// once (last release), so the teardown must mirror exactly what the lock wrote —
// this flag is that link, not a per-caller preference. Consequently, in the rare
// overlap where a non-instant lock layers on top of an instant one (or vice versa),
// the first lock's mode governs the whole stack; that's correct, since there is only
// one shared transition to tear down. Reset on the empty-stack release for hygiene.
let animatedTintWritten = false

export interface UseBodyScrollLockOptions {
  /**
   * Skip the animated `<html>` background tint and snap it in a single paint instead
   * (set on lock, cleared synchronously on release — no transition/timeout).
   *
   * The production timeline (issue #21022) scrolls the *window* and is dense with
   * sticky headers, so flipping body to position:fixed already forces a full-page
   * reflow; layering an animated whole-page background-color change on top of that
   * reads as an "overall flicker" on iPad Safari. Snapping the tint removes it.
   * Other dialogs/poppers leave this off and keep the smooth fade.
   */
  instantBackdrop?: boolean
}

/**
 * Locks background scrolling while `active` is true.
 *
 * MUI's built-in scroll lock (disableScrollLock) is disabled across the app because it
 * adds padding-right to the body to compensate for the scrollbar, which conflicts with
 * scrollbarGutter: 'stable' on html and causes a white bar / layout shift. We handle
 * scroll prevention ourselves using position:fixed on the body, and tint the html
 * background so the scrollbar gutter matches the backdrop (no shift, only color change).
 */
export function useBodyScrollLock(active: boolean, opts?: UseBodyScrollLockOptions) {
  const instantBackdrop = opts?.instantBackdrop === true

  useEffect(() => {
    if (!active) return

    if (lockCount === 0) {
      savedScrollY = window.scrollY
      document.body.style.position = 'fixed'
      document.body.style.top = `-${savedScrollY}px`
      document.body.style.width = '100%'
      document.body.style.minHeight = '100vh'
      // Smoothly tint the html background so the scrollbar gutter matches the backdrop.
      // The gutter always stays (no shift), only its color changes. With instantBackdrop
      // we skip the transition so the tint snaps in a single paint (no whole-page
      // background animation — see UseBodyScrollLockOptions.instantBackdrop).
      animatedTintWritten = !instantBackdrop
      if (animatedTintWritten) {
        document.documentElement.style.transition = `background-color ${BACKDROP_TRANSITION_MS}ms`
      }
      document.documentElement.style.backgroundColor = 'rgba(0, 0, 0, 0.5)'
    }
    lockCount++

    return () => {
      lockCount = Math.max(0, lockCount - 1)
      if (lockCount > 0) return

      const scrollY = savedScrollY
      const wasAnimated = animatedTintWritten
      animatedTintWritten = false
      const restore = () => {
        if (typeof document === 'undefined') return
        document.body.style.position = ''
        document.body.style.top = ''
        document.body.style.width = ''
        document.body.style.minHeight = ''
        document.documentElement.style.transition = ''
        window.scrollTo(0, scrollY)
      }

      document.documentElement.style.backgroundColor = ''

      // Mirror the lock: if we never wrote the fade, snap the restore in the same paint.
      // Otherwise wait for the fade to finish before unfixing the body.
      if (!wasAnimated) {
        restore()
        return
      }
      setTimeout(restore, BACKDROP_TRANSITION_MS)
    }
  }, [active])
}
